import React, { useEffect, useState } from 'react';
import { storyService } from '../services/api';
import { useAuthStore } from '../store/useAuthStore';
import RichTextEditor from './RichTextEditor';
import styles from './StoriesPanel.module.css';

// Strip Quill-internal elements and fix the HTML before rendering
function cleanQuillHtml(html) {
    if (!html) return '';
    return html
        .replace(/<div class="ql-clipboard"[^>]*>[\s\S]*?<\/div>/gi, '')
        .replace(/<div class="ql-tooltip[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
        .replace(/\scontenteditable="[^"]*"/gi, '')
        .replace(/\sdata-gramm="[^"]*"/gi, '')
        .trim();
}

function StoryBody({ content }) {
    if (!content) return null;

    // Detect HTML content (from RichTextEditor)
    const isHtml = /<[a-z][\s\S]*>/i.test(content);

    if (isHtml) {
        return (
            <div
                className="story-html-body"
                dangerouslySetInnerHTML={{ __html: cleanQuillHtml(content) }}
            />
        );
    }

    // Plain text (saved before RichTextEditor was added): preserve newlines
    return (
        <div className="story-html-body">
            {content.split('\n').map((line, i, arr) => (
                <React.Fragment key={i}>
                    {line}
                    {i < arr.length - 1 && <br />}
                </React.Fragment>
            ))}
        </div>
    );
}

export default function StoriesPanel({ protestId, canEdit }) {
    const user = useAuthStore((s) => s.user);
    const [stories,   setStories]   = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [adding,    setAdding]    = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [saving,    setSaving]    = useState(false);
    const [form,      setForm]      = useState({ title: '', content: '' });
    const [error,     setError]     = useState('');

    useEffect(() => {
        storyService.list(protestId)
            .then((r) => setStories(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [protestId]);

    const openAdd = () => { setForm({ title: '', content: '' }); setAdding(true); setEditingId(null); };

    const openEdit = (s) => {
        setForm({ title: s.title, content: s.content });
        setEditingId(s.id);
        setAdding(false);
    };

    const cancelForm = () => { setAdding(false); setEditingId(null); setError(''); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            if (editingId) {
                const res = await storyService.update(protestId, editingId, form);
                setStories((prev) => prev.map((s) => (s.id === editingId ? res.data : s)));
                setEditingId(null);
            } else {
                const res = await storyService.create(protestId, form);
                setStories((prev) => [...prev, res.data]);
                setAdding(false);
            }
            setForm({ title: '', content: '' });
        } catch (err) {
            setError(err.response?.data?.message || 'فشل الحفظ');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('حذف هذه القصة؟')) return;
        await storyService.delete(protestId, id);
        setStories((prev) => prev.filter((s) => s.id !== id));
    };

    const canEditStory = (s) => user?.role === 'super_admin' || user?.id === s.created_by;

    const StoryForm = () => (
        <form onSubmit={handleSubmit} className={styles.addForm}>
            {error && <div className={styles.error}>{error}</div>}
            <input
                className={styles.input}
                placeholder="عنوان القصة *"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
            />
            <RichTextEditor
                value={form.content}
                onChange={(content) => setForm((f) => ({ ...f, content }))}
                placeholder="محتوى القصة..."
            />
            <div className={styles.formActions}>
                <button type="submit" className={styles.saveBtn} disabled={saving}>
                    {saving ? 'جاري الحفظ...' : 'حفظ'}
                </button>
                <button type="button" className={styles.cancelBtn} onClick={cancelForm}>إلغاء</button>
            </div>
        </form>
    );

    if (loading) return <div className={styles.state}>جاري التحميل...</div>;

    return (
        <div className={`${styles.panel} text-ar`}>
            <div className={styles.panelHeader}>
                <h3 className={styles.panelTitle}>القصص ({stories.length})</h3>
                {canEdit && !adding && !editingId && (
                    <button className={styles.addBtn} onClick={openAdd}>+ إضافة قصة</button>
                )}
            </div>

            {adding && <StoryForm />}

            {stories.length === 0 && !adding && (
                <p className={styles.empty}>لا توجد قصص مضافة بعد</p>
            )}

            <div className={styles.list}>
                {stories.map((s) => (
                    <div key={s.id} className={styles.item}>
                        {editingId === s.id ? (
                            <StoryForm />
                        ) : (
                            <>
                                <div className={styles.itemHeader}>
                                    <h4 className={styles.storyTitle}>{s.title}</h4>
                                    <div className={styles.itemActions}>
                                        {canEditStory(s) && (
                                            <>
                                                <button className={styles.editBtn} onClick={() => openEdit(s)}>✏</button>
                                                <button className={styles.deleteBtn} onClick={() => handleDelete(s.id)}>🗑</button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <StoryBody content={s.content} />
                                <div className={styles.storyMeta}>
                                    {s.creator && <span>بقلم: {s.creator.name}</span>}
                                    <span>{new Date(s.created_at).toLocaleDateString('ar-SY')}</span>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
